// Pointer tracking for a tilting card (gift-card-collection hover). It listens
// on the card's untilted parent and measures against the card's layout box, so
// the tilt can't move the edge out from under the pointer and make it flicker.
// Sets --gx/--gy for the glare and data-hover while the pointer is over the card.

type Options = {
  /** Pointer position, 0–1 from the top-left corner of the tracked box. */
  onMove?: (x: number, y: number) => void;
  onLeave?: () => void;
  /** No transform when the visitor prefers reduced motion. */
  reducedMotion?: boolean;
  /**
   * 'stage': track the whole parent and only report it (plus data-hover); the
   * caller owns the transform and glare.
   */
  track?: 'card' | 'stage';
};

export const attachCardTilt = (
  card: HTMLElement,
  { onMove, onLeave, reducedMotion = false, track = 'card' }: Options = {},
): (() => void) => {
  const stage = card.parentElement;
  if (!stage) return () => {};
  const tilt = track === 'card';
  let over = false;

  const leave = () => {
    if (!over) return;
    over = false;
    delete card.dataset.hover;
    if (tilt) card.style.transform = '';
    onLeave?.();
  };
  const move = (e: PointerEvent) => {
    const r = stage.getBoundingClientRect();
    if (!tilt) {
      over = true;
      card.dataset.hover = '';
      const clamp = (v: number) => Math.max(0, Math.min(1, v));
      onMove?.(
        clamp((e.clientX - r.left) / r.width),
        clamp((e.clientY - r.top) / r.height),
      );
      return;
    }
    const x = (e.clientX - r.left - card.offsetLeft) / card.offsetWidth;
    const y = (e.clientY - r.top - card.offsetTop) / card.offsetHeight;
    if (x < 0 || x > 1 || y < 0 || y > 1) return leave();
    over = true;
    card.dataset.hover = '';
    card.style.setProperty('--gx', `${x * 100}%`);
    card.style.setProperty('--gy', `${y * 100}%`);
    if (!reducedMotion)
      card.style.transform = `rotateX(${((0.5 - y) * 6).toFixed(
        2,
      )}deg) rotateY(${((x - 0.5) * 7).toFixed(2)}deg)`;
    onMove?.(x, y);
  };

  stage.addEventListener('pointermove', move);
  stage.addEventListener('pointerleave', leave);
  return () => {
    stage.removeEventListener('pointermove', move);
    stage.removeEventListener('pointerleave', leave);
    delete card.dataset.hover;
    if (tilt) card.style.transform = '';
  };
};
